import React, { useEffect, useState } from 'react'
import { api, apiJson } from '../client'
import { Card } from '../shared/components/Card'
import { FormField } from '../shared/components/FormField'
import { Banner } from '../shared/components/Banner'

type Flag = {
  id: string
  key: string
  description?: string
  enabled_global?: boolean
}

export default function FeatureFlagSwitchboard() {
  const [flags, setFlags] = useState<Flag[] | null>(null)
  const [msg, setMsg] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const localOnly =
    import.meta.env.MODE === 'development' ||
    location.hostname === 'localhost' ||
    location.hostname === '127.0.0.1'

  const load = async () => {
    setError(null)
    try {
      // NOTE: No /v1 prefix here — client.ts adds the base (/v1) for us
      const data = await apiJson<Flag[]>('/admin/feature-flags', {}, true)
      setFlags(Array.isArray(data) ? data : [])
    } catch (e: any) {
      // Don’t crash if backend returns HTML or !ok — client.ts surfaces a readable message
      setFlags([])
      setError(e?.message || 'Failed to load feature flags')
    }
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        await load()
      } catch {
        /* handled in load() */
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const toggle = async (key: string, next: boolean) => {
    if (!localOnly) {
      setMsg('Toggle is restricted to local/dev environments')
      return
    }
    setMsg(undefined)
    try {
      // Use raw api() because we don’t care about the body; let load() refresh the list.
      await api(`/admin/feature-flags/${encodeURIComponent(key)}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      })
    } catch (e: any) {
      setMsg(e?.message || 'Failed to toggle flag')
    } finally {
      await load()
    }
  }

  return (
    <Card title="Feature Flags (local/dev only)">
      {msg && <Banner tone="warn" message={msg} />}
      {error && <Banner tone="error" message={error} />}
      {!flags && !error && (
        <div className="py-3 text-xs text-gray-600">Loading flags…</div>
      )}
      {flags && flags.length === 0 && !error && (
        <div className="py-3 text-xs text-gray-600">No flags found.</div>
      )}
      {flags && flags.length > 0 && (
        <div className="divide-y">
          {flags.map((f) => (
            <div key={f.id ?? f.key} className="py-2 flex items-center justify-between">
              <div>
                <div className="font-mono text-sm">{f.key}</div>
                {f.description && (
                  <div className="text-xs text-gray-500">{f.description}</div>
                )}
              </div>
              <FormField label="Enabled (global)">
                <input
                  type="checkbox"
                  checked={!!f.enabled_global}
                  onChange={(e) => toggle(f.key, e.target.checked)}
                />
              </FormField>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}